import { Popover, Toast } from '@byecode/ui'
import type { DragEndEvent } from '@dnd-kit/core'
import { DevicePreviewEnum, DTFile } from '@lighthouse/core'
import { arrayMove } from '@lighthouse/tools'
import { FILE_STATES, useAbortAll } from '@rpldy/uploady'
import { clone, concat, find, findIndex, isEmpty } from 'rambda'
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMount } from 'react-use'
import { useImmer } from 'use-immer'

import { errorCodeMap } from '../../../constants'
import { useApplicationContext } from '../../../contexts'
import type { EnableActionsParams, FetchPexelsParams, PexelsResponse, WithStatusFile } from '../../../types'
import { getBatchToInnerFile, getImageFullUrlInApplication } from '../../../utils'
import { getFileNameByUrl, getFileSizeByUrl, getFileTypeByUrl } from '../../../utils/helper'
import { FilePreviewer } from '../../FilePreviewer'
import { SortableList } from '../../List'
import type { UseUploadFileSParameter } from '../../UploadManage'
import { useUploadBatchError, useUploadBatchFinished, useUploadBatchProgress, useUploadBatchUploading } from '../../UploadManage'
import { useUploadBatch } from '../../UploadManage/hooks/useUploadBatch'
import { FileUploader } from '../FileUploader'
import FileListItem from './FileListItem'
import * as SC from './styles'

interface FileListProps {
    id?: string
    children?: React.ReactNode
    accept?: string
    isPexels?: boolean
    enableItemActions?: EnableActionsParams
    files: DTFile[]
    autoOpenWhenEmpty?: boolean
    previewType: DevicePreviewEnum
    uploadyOptions: Pick<UseUploadFileSParameter, 'info' | 'options'>
    disableUpload?: boolean
    isUsedLink?: boolean
    popoverMainAxis?: boolean
    multiple?: boolean
    onChange?: (files: DTFile[]) => void
    onFetchPexelsList?: (params: FetchPexelsParams) => Promise<PexelsResponse>
}

export const FileListPreviewer: React.FC<FileListProps> = ({
    id,
    accept,
    isPexels,
    files,
    enableItemActions,
    previewType,
    autoOpenWhenEmpty,
    disableUpload,
    uploadyOptions,
    isUsedLink,
    multiple,
    onChange,
    onFetchPexelsList
}) => {
    const [defaultIndex, setDefaultIndex] = useState(0)
    const [opened, setOpened] = useState(false)
    const [popoverOpen, setPopoverOpen] = useState(false)
    const { appId } = useApplicationContext()
    const { t } = useTranslation()
    const uId = useId()
    const targetId = uId
    const uploadDropId = id || uId
    const { batchItems, removeBatchItem } = useUploadBatch(uploadDropId, FILE_STATES.UPLOADING)
    const getBatchBizKey = useCallback((batchItem: any) => {
        const file = batchItem?.file
        const name = file?.name || ''
        const type = getFileTypeByUrl(name)
        const batchId = batchItem?.batchId || 'no-batch'
        return `${batchId}:sig:${name}:${Number(file?.size ?? 0)}:${type}:${Number(file?.lastModified ?? 0)}`
    }, [])
    const getUploadFileKey = useCallback((file: Pick<WithStatusFile, 'name' | 'size' | 'type'>) => {
        return (file as any).__bizKey || `${file.name}:${Number(file.size ?? 0)}:${file.type}`
    }, [])
    const normalizeDraftFiles = useCallback(
        (draft: any[]) => {
            const seenUploadingKey = new Set<string>()
            const next: WithStatusFile[] = []

            draft.forEach(item => {
                if (item.status === 'uploading') {
                    const key = getUploadFileKey(item)
                    if (seenUploadingKey.has(key)) return
                    seenUploadingKey.add(key)
                    next.push(item)
                    return
                }
                next.push(item)
            })
            draft.splice(0, draft.length, ...next)
        },
        [getUploadFileKey]
    )
    const listData = useMemo(() => {
        const uniqueBatchItems = [...(batchItems ?? [])]
            .reduce((acc: Map<string, any>, batchItem: any) => {
                const { file, state, completed, loaded } = batchItem
                const key = getBatchBizKey(batchItem)

                const prev = acc.get(key)
                if (!prev) {
                    acc.set(key, batchItem)
                    return acc
                }

                const prevIsUploading = prev.state === FILE_STATES.UPLOADING
                const curIsUploading = state === FILE_STATES.UPLOADING
                const prevCompleted = prev.completed ?? 0
                const curCompleted = completed ?? 0
                const prevLoaded = prev.loaded ?? 0
                const curLoaded = loaded ?? 0

                // 优先保留真正上传中的那条；否则保留进度更大的
                if ((curIsUploading && !prevIsUploading) || curCompleted > prevCompleted || curLoaded > prevLoaded) {
                    acc.set(key, batchItem)
                }
                return acc
            }, new Map<string, any>())
            .values()

        const dedupedList = [...uniqueBatchItems].map(batchItem => {
            const key = getBatchBizKey(batchItem)
            return { ...getBatchToInnerFile(batchItem), __bizKey: key }
        })

        return [
            ...files.map(url => {
                const name = getFileNameByUrl(url) || ''
                const type = getFileTypeByUrl(url)
                const size = getFileSizeByUrl(url)
                return {
                    uid: url,
                    label: name,
                    value: url,
                    size: Number(size),
                    type,
                    url: getImageFullUrlInApplication(appId, url),
                    name: name ?? '',
                    status: 'success',
                    percent: 100
                }
            }),
            ...dedupedList
        ]
    }, [appId, batchItems, files, getBatchBizKey])
    const [innerFiles, setInnerFiles] = useImmer(listData)
    const isMounted = useRef({
        mount: true,
        files: innerFiles.map(item => item.url).filter(Boolean)
    }) // 初始化为 true，表示组件已挂载
    // useUpdateEffect(() => {
    //     setInnerFiles(listData)
    // }, [listData])

    const newUploadyOptions = useMemo(
        () => ({ ...uploadyOptions, info: { ...uploadyOptions.info, id: uploadDropId } }),
        [uploadDropId, uploadyOptions]
    )

    useEffect(() => {
        // 在组件卸载时更新 isMounted 的值
        return () => {
            isMounted.current = {
                mount: false,
                files: innerFiles.map(item => item.value).filter(Boolean)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const abortAll = useAbortAll()
    // const abortBatch = useAbortBatch()
    useUploadBatchUploading(
        uploadDropId,
        (batchItems, batchId, targetId) => {
            const currentBatchItems = batchItems.filter(item => item.batchId === batchId)
            const hasNotAllowFileType =
                accept && accept !== '*'
                    ? currentBatchItems.some(item => {
                          const { file } = item
                          const fileType = getFileTypeByUrl(file.name)
                          return !accept.includes(fileType)
                      })
                    : false
            if (hasNotAllowFileType) {
                Toast.info(t('unsupportedFormatOnlyImageUploads'))
                abortAll()
                return
            }
            // 上传态里 uploady 可能会对同一个物理文件产生多个 batch item（uid 不同，state 不同）。
            // 这里按“物理文件键”去重，确保 UI 里最多一条上传中的同名同文件项。
            const uniqueBatchItems = [...currentBatchItems].reduce((acc, item) => {
                const { file, state, completed, loaded, id } = item
                const key = getBatchBizKey(item)

                const prev = acc.get(key)
                if (!prev) {
                    acc.set(key, item)
                    return acc
                }

                const prevIsUploading = prev.state === FILE_STATES.UPLOADING
                const curIsUploading = state === FILE_STATES.UPLOADING
                const prevCompleted = prev.completed ?? 0
                const curCompleted = completed ?? 0
                const prevLoaded = prev.loaded ?? 0
                const curLoaded = loaded ?? 0

                if ((curIsUploading && !prevIsUploading) || curCompleted > prevCompleted || curLoaded > prevLoaded) {
                    acc.set(key, item)
                }
                return acc
            }, new Map<string, any>())

            type UploadPreviewFile = {
                uid: string
                name: string
                label: string
                value: string
                type: WithStatusFile['type']
                url: string
                size: number
                status: 'uploading'
                percent: number
            }

            const list: UploadPreviewFile[] = [...uniqueBatchItems.values()].map((item: any) => {
                const { file, id, completed } = item
                const { name, size } = file
                const fileType = getFileTypeByUrl(name)
                const sizeNum = Number(size)
                const percentNum = Number(completed ?? 0)
                return {
                    uid: id as string,
                    name,
                    label: name,
                    value: '',
                    type: fileType,
                    status: 'uploading' as const,
                    url: '',
                    size: Number.isFinite(sizeNum) ? sizeNum : 0,
                    percent: Number.isFinite(percentNum) ? percentNum : 0,
                    __bizKey: getBatchBizKey(item)
                }
            })

            const sorted = [...list].sort((a, b) => (b.percent ?? 0) - (a.percent ?? 0))
            const items = multiple ? sorted : sorted.slice(0, 1)
            setInnerFiles(draft => {
                items.forEach(item => {
                    const key = getUploadFileKey(item)
                    const existedIndex = findIndex(f => f.status === 'uploading' && getUploadFileKey(f) === key, draft)

                    if (existedIndex >= 0) {
                        // 只替换进度更大的那条；但仍以最新 uid 为准，避免后续 listener 命不中
                        const prevPercent = draft[existedIndex].percent ?? 0
                        const nextPercent = item.percent ?? 0
                        if (nextPercent >= prevPercent) {
                            draft[existedIndex] = item
                        }
                        return
                    }

                    // fallback：如果 uid 也匹配上了，直接覆盖
                    const uidIndex = findIndex(f => f.uid === item.uid, draft)
                    if (uidIndex >= 0) {
                        draft[uidIndex] = item
                        return
                    }
                    draft.push(item)
                })
                normalizeDraftFiles(draft)
            })
        },
        targetId
    )

    useUploadBatchError(uploadDropId, batchItems => {
        setInnerFiles(draft => {
            draft.forEach(item => {
                const { uid } = item
                const uploadingFileIndex = findIndex(({ id }) => uid === id, batchItems)
                if (uploadingFileIndex !== -1) {
                    const uploadFile = draft[uploadingFileIndex]
                    if (uploadFile) {
                        uploadFile.value = uid
                        uploadFile.status = 'error'
                        uploadFile.percent = 100
                    }
                }
            })
            normalizeDraftFiles(draft)
        })
        console.log(
            '[FLP progress]',
            batchItems.map(item => ({
                batchId: item.batchId,
                key: getBatchBizKey(item),
                uid: item.id,
                state: item.state,
                completed: item.completed
            }))
        )
    })

    useUploadBatchProgress(uploadDropId, batchItems => {
        const validProgressItems = batchItems.filter(item => {
            const completed = Number(item.completed ?? 0)
            return item.state === FILE_STATES.UPLOADING || item.state === FILE_STATES.PENDING || completed > 0
        })
        if (validProgressItems.length === 0) {
            console.log(
                '[FLP progress]',
                batchItems.map(item => ({
                    batchId: item.batchId,
                    key: getBatchBizKey(item),
                    uid: item.id,
                    state: item.state,
                    completed: item.completed
                }))
            )
            return
        }

        const uniqueBatchItems = [...validProgressItems].reduce((acc, item) => {
            const { file, state, completed, loaded } = item
            const key = getBatchBizKey(item)
            const prev = acc.get(key)
            if (!prev) {
                acc.set(key, item)
                return acc
            }
            const prevIsUploading = prev.state === FILE_STATES.UPLOADING
            const curIsUploading = state === FILE_STATES.UPLOADING
            const prevCompleted = prev.completed ?? 0
            const curCompleted = completed ?? 0
            const prevLoaded = prev.loaded ?? 0
            const curLoaded = loaded ?? 0
            if ((curIsUploading && !prevIsUploading) || curCompleted > prevCompleted || curLoaded > prevLoaded) {
                acc.set(key, item)
            }
            return acc
        }, new Map<string, any>())
        const mergedBatchItems = [...uniqueBatchItems.values()]
        const batchItemMap = [...uniqueBatchItems.entries()].reduce((acc, [key, item]) => {
            acc.set(key, item)
            return acc
        }, new Map<string, any>())
        const idToBizKey = mergedBatchItems.reduce((acc, item) => {
            acc.set(item.id, getBatchBizKey(item))
            return acc
        }, new Map<string, string>())
        setInnerFiles(draft => {
            draft.forEach(item => {
                const uploadingFile = batchItemMap.get(getUploadFileKey(item)) || find(({ id }) => item.uid === id, mergedBatchItems)
                if (uploadingFile) {
                    item.status = 'uploading'
                    item.percent = uploadingFile.completed
                    if (item.uid !== uploadingFile.id) {
                        item.uid = uploadingFile.id
                    }
                    ;(item as any).__bizKey = idToBizKey.get(uploadingFile.id) || getUploadFileKey(item)
                }
            })
            normalizeDraftFiles(draft)
        })
    })

    useUploadBatchFinished(
        uploadDropId,
        (batchItems, _, tId) => {
            if (tId !== targetId) {
                return
            }
            if (!isMounted.current.mount) {
                const uploadFileUrls = batchItems
                    .map(item => {
                        const { uploadResponse } = item
                        return uploadResponse.data?.content?.url as string
                    })
                    .filter(Boolean)
                const fileUrls = [...isMounted.current.files, ...uploadFileUrls]
                removeBatchItem(uploadDropId, uploadFileUrls)
                onChange?.(fileUrls.filter(Boolean))
                return
            }
            setInnerFiles(draft => {
                batchItems.forEach(batch => {
                    const { uploadResponse, id } = batch
                    const finishedKey = getBatchBizKey(batch)

                    const sameUploadingIndexes = draft.reduce<number[]>((acc, item, idx) => {
                        if (item.status === 'uploading' && getUploadFileKey(item) === finishedKey) {
                            acc.push(idx)
                        }
                        return acc
                    }, [])

                    const idIndex = findIndex(({ uid }) => uid === id, draft)
                    const keepIndex = idIndex >= 0 ? idIndex : sameUploadingIndexes[0]
                    if (keepIndex === undefined || keepIndex < 0) {
                        return
                    }
                    const uploadFile = draft[keepIndex]
                    const httpCode = uploadResponse.data?.code
                    if (uploadResponse.data?.code === '0000000') {
                        const url = uploadResponse.data?.content ? getImageFullUrlInApplication(appId, uploadResponse.data.content.url) : ''
                        uploadFile.percent = 100
                        uploadFile.value = uploadResponse.data.content.url || id
                        uploadFile.url = url
                        uploadFile.status = uploadResponse.data?.content ? 'success' : 'error'
                        ;(uploadFile as any).__bizKey = finishedKey
                    } else {
                        Toast.error(errorCodeMap[httpCode as keyof typeof errorCodeMap] || '未知错误')
                        uploadFile.value = ''
                        uploadFile.status = 'error'
                        uploadFile.percent = 100
                        ;(uploadFile as any).__bizKey = finishedKey
                    }

                    sameUploadingIndexes
                        .filter(idx => idx !== keepIndex)
                        .sort((a, b) => b - a)
                        .forEach(i => {
                            draft.splice(i, 1)
                        })
                })
                normalizeDraftFiles(draft)
                onChange?.(draft.map(v => v.value).filter(Boolean))
            })
        },
        targetId
    )
    const handleFileAdd = useCallback(
        (file: WithStatusFile) => {
            setInnerFiles(draft => {
                const newFile = { ...file, label: file.name, uid: file.name, value: file.value, size: Number(file.size), percent: 100 }
                draft.push(newFile)
                onChange?.(concat(files, [newFile.value]))
            })
            setPopoverOpen(false)
        },
        [files, onChange, setInnerFiles]
    )

    const handleFileSort = useCallback(
        (ev: DragEndEvent) => {
            const {
                active: { id: activeId },
                over
            } = ev
            const overId = over?.id
            if (!overId) {
                return
            }

            const sourceIndex = findIndex(file => file.uid === activeId, innerFiles)
            const targetIndex = findIndex(file => file.uid === overId, innerFiles)

            const sortedFiles = arrayMove(innerFiles, sourceIndex, targetIndex)

            setInnerFiles(sortedFiles)

            onChange?.(sortedFiles.map(file => file.value).filter(Boolean))
        },
        [innerFiles, onChange, setInnerFiles]
    )

    const handleFileRemove = useCallback(
        (index: number) => {
            setInnerFiles(draft => {
                removeBatchItem(uploadDropId, [draft[index].uid])
                draft.splice(index, 1)
                onChange?.(clone(draft).map(item => item.value))
            })
        },
        [onChange, removeBatchItem, setInnerFiles, uploadDropId]
    )

    // 如果没有文件，则默认打开文件上传器
    useMount(() => {
        if (autoOpenWhenEmpty && isEmpty(innerFiles)) {
            setPopoverOpen(true)
        }
    })

    useEffect(() => {
        return () => {
            setPopoverOpen(false)
        }
    }, [])

    const renderFileListItem = useCallback(
        (item: WithStatusFile, index: number) => {
            return (
                <FileListItem
                    enableActions={enableItemActions}
                    data={item}
                    previewType={previewType}
                    onRemove={() => {
                        handleFileRemove(index)
                    }}
                    onPreview={() => {
                        setOpened(true)
                        setDefaultIndex(index)
                    }}
                />
            )
        },
        [enableItemActions, handleFileRemove, previewType]
    )

    const listContent = useMemo(() => {
        return innerFiles.length === 0 ? (
            <></>
        ) : (
            <SortableList
                rowKey="uid"
                onSortEnd={handleFileSort}
                data={innerFiles}
                itemRender={(item, index) => renderFileListItem(item as WithStatusFile, index)}
            />
        )
    }, [handleFileSort, innerFiles, renderFileListItem])

    const emptyArr = useMemo(() => {
        return []
    }, [])

    return (
        <SC.FileListWrapper>
            <SC.FileListContainer>{listContent}</SC.FileListContainer>
            {!disableUpload && (
                <Popover width="auto" withinPortal opened={popoverOpen} onChange={setPopoverOpen}>
                    <Popover.Target>
                        <SC.AddFileBtn>
                            <SC.AddIcon type="Add" />
                            <SC.AddBtnText>添加文件</SC.AddBtnText>
                        </SC.AddFileBtn>
                    </Popover.Target>
                    <Popover.Dropdown>
                        <FileUploader
                            accept={accept}
                            multiple={multiple}
                            isPexels={isPexels}
                            isUsedLink={isUsedLink}
                            uploadyOptions={newUploadyOptions}
                            onImageLinkSave={handleFileAdd}
                            onFetchPexelsList={onFetchPexelsList}
                        />
                    </Popover.Dropdown>
                </Popover>
            )}
            <FilePreviewer defaultIndex={defaultIndex} fileList={innerFiles} opened={opened} onClose={() => setOpened(false)} />
        </SC.FileListWrapper>
    )
}
